// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { List, ListSubheader, MenuItem, Popover, Tooltip } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { OPTIONS_SELECTOR_MENU_ITEM_CLASS } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useAppSelector } from '@advisor-app/store/store';
import { selectClientProductsAccounts } from '@shared/data-access/slices/clients/clients-selectors';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type TradeAccountSide = 'from' | 'to';

export interface TradeAccountSelectProps {
  readonly iban?: string | null;
  readonly side: TradeAccountSide;
  readonly rowIndex: number;
  readonly onAccountChange?: (
    index: number,
    side: TradeAccountSide,
    iban: string,
    institution: { id: string; name: string },
  ) => void;
}

type AccountOption = {
  iban: string;
  name: string;
  institutionId: string;
  institutionName: string;
  label: string;
};

const maskIban = (iban: string): string => `****${iban.slice(-4)}`;

const useTradeAccountSelect = ({
  iban,
  side,
  rowIndex,
  onAccountChange,
}: TradeAccountSelectProps) => {
  const { t } = useTranslation();
  const institutions = useAppSelector(selectClientProductsAccounts);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const accounts = useMemo<AccountOption[]>(
    () =>
      institutions.flatMap(institution =>
        institution.accounts.map(account => {
          const name = account.name?.trim() || account.type || account.iban;
          return {
            iban: account.iban,
            name,
            institutionId: institution.id,
            institutionName: institution.name,
            label: `${name} · ${maskIban(account.iban)}`,
          };
        }),
      ),
    [institutions],
  );

  const selected = useMemo(
    () => accounts.find(account => account.iban === iban),
    [accounts, iban],
  );

  const groupedAccounts = useMemo(() => {
    const groups: Array<{ name: string; accounts: AccountOption[] }> = [];

    accounts.forEach(account => {
      const group = groups.find(item => item.name === account.institutionName);
      if (group) {
        group.accounts.push(account);
        return;
      }

      groups.push({ name: account.institutionName, accounts: [account] });
    });

    return groups;
  }, [accounts]);

  const hasAccounts = accounts.length > 0;
  const isOpen = Boolean(anchorEl);
  const hasMissingIban = Boolean(iban) && !selected;
  const isPlaceholder = !selected && !iban;
  const institutionLine = selected?.institutionName;
  const accountLine = selected ? selected.label : iban ? maskIban(iban) : '';
  const changeAccountTooltip = t('trader.accounts.change-account', {
    ns: 'components',
  });
  const testId = `trade-account-select-${side}`;
  const menuPaperClassName =
    'min-w-64 !mt-1 !flex !h-auto !max-h-[550px] !flex-col !overflow-hidden !rounded-xl !border !border-solid !border-black/25 !bg-page !shadow-none';
  const menuPaperSx = {
    maxHeight: '550px !important',
  } as const;
  const menuHeaderClassName =
    'flex shrink-0 items-center border-b border-solid border-black/25 bg-page p-4 font-medium leading-7 text-border-dark';
  const menuListClassName = 'min-h-0 flex-1 !h-[500px] overflow-y-auto p-4';
  const menuAnchorOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
  } as const;
  const menuTransformOrigin = {
    vertical: 'top',
    horizontal: 'center',
  } as const;
  const triggerClassName =
    'flex w-full min-w-0 items-center gap-1 rounded p-0.5 text-left focus:outline-none';

  const onOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onSelect = useCallback(
    (account: AccountOption) => {
      onAccountChange?.(rowIndex, side, account.iban, {
        id: account.institutionId,
        name: account.institutionName,
      });
      setAnchorEl(null);
    },
    [onAccountChange, rowIndex, side],
  );

  return {
    accountLine,
    changeAccountTooltip,
    groupedAccounts,
    hasAccounts,
    hasMissingIban,
    institutionLine,
    isOpen,
    isPlaceholder,
    menuAnchorOrigin,
    menuHeaderClassName,
    menuListClassName,
    menuPaperClassName,
    menuPaperSx,
    menuTransformOrigin,
    onClose,
    onOpen,
    onSelect,
    selectedIban: iban,
    testId,
    triggerClassName,
    triggerEl: anchorEl,
  };
};

const MenuGroups = ({
  groupedAccounts,
  onSelect,
  selectedIban,
}: Pick<
  ReturnType<typeof useTradeAccountSelect>,
  'groupedAccounts' | 'onSelect' | 'selectedIban'
>) => (
  <>
    {groupedAccounts.map(group => (
      <div key={group.name}>
        <ListSubheader
          disableGutters
          disableSticky
          className="!cursor-default !truncate !bg-page !font-medium !leading-7 !text-border-dark"
        >
          {group.name}
        </ListSubheader>
        {group.accounts.map(account => (
          <MenuItem
            key={account.iban}
            selected={account.iban === selectedIban}
            onClick={() => onSelect(account)}
            data-testid={`trade-account-option-${account.iban}`}
            className={OPTIONS_SELECTOR_MENU_ITEM_CLASS}
          >
            {account.label}
          </MenuItem>
        ))}
      </div>
    ))}
  </>
);

export const TradeAccountSelect = defineComponent({
  displayName: 'TradeAccountSelect',
  useSetup: useTradeAccountSelect,
  Slots: { MenuGroups },
  View: ({
    accountLine,
    changeAccountTooltip,
    groupedAccounts,
    hasAccounts,
    hasMissingIban,
    institutionLine,
    isOpen,
    isPlaceholder,
    menuAnchorOrigin,
    menuHeaderClassName,
    menuListClassName,
    menuPaperClassName,
    menuPaperSx,
    menuTransformOrigin,
    onClose,
    onOpen,
    onSelect,
    selectedIban,
    testId,
    triggerClassName,
    triggerEl,
    Slots,
  }) =>
    hasAccounts ? (
      <div className="min-w-0">
        <button
          type="button"
          data-testid={testId}
          aria-label={changeAccountTooltip}
          aria-expanded={isOpen}
          onClick={onOpen}
          className={triggerClassName}
        >
          <span
            className={classNames('flex min-w-0 flex-1 flex-col', {
              'text-negative': hasMissingIban,
            })}
          >
            {institutionLine ? (
              <span className="truncate">{institutionLine}</span>
            ) : isPlaceholder ? (
              <span className="truncate text-sidebar-text">
                <Translation i18nKey="common.select-account" ns="common" />
              </span>
            ) : null}
            {accountLine ? (
              <span
                className={classNames('truncate text-sm', {
                  'text-sidebar-text': isPlaceholder,
                })}
              >
                {accountLine}
              </span>
            ) : null}
          </span>
          <Tooltip
            title={changeAccountTooltip}
            placement="top"
            disableHoverListener={isOpen}
          >
            <span
              className="flex shrink-0 items-center self-stretch"
              data-testid={`${testId}-chevron`}
            >
              <AppIcon
                icon="ChevronDown"
                size="xl"
                color="inherit"
                className="block shrink-0"
                aria-hidden
              />
            </span>
          </Tooltip>
        </button>
        <Popover
          open={isOpen}
          anchorEl={triggerEl}
          onClose={onClose}
          anchorOrigin={menuAnchorOrigin}
          transformOrigin={menuTransformOrigin}
          classes={{ paper: menuPaperClassName }}
          slotProps={{
            paper: {
              sx: menuPaperSx,
            },
          }}
        >
          <div className={menuHeaderClassName} data-testid={`${testId}-header`}>
            <Translation i18nKey="common.select-account" ns="common" />
          </div>
          <div className={menuListClassName} data-testid={`${testId}-menu`}>
            <List dense disablePadding>
              <Slots.MenuGroups
                groupedAccounts={groupedAccounts}
                onSelect={onSelect}
                selectedIban={selectedIban}
              />
            </List>
          </div>
        </Popover>
      </div>
    ) : (
      <span>-</span>
    ),
});
